import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { Expense } from "@/hooks/useExpenses";
import type { Trip } from "@/hooks/useTrips";

interface Vehicle {
  id: string;
  name: string;
}

interface TaxesTabProps {
  expenses: Expense[];
  trips: Trip[];
  vehicles: Vehicle[];
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

const formatDate = (d: string | null) => {
  if (!d) return null;
  const date = new Date(d);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

interface CategoryRow {
  category: string;
  amount: number;
}

interface VehicleSummary {
  vehicleId: string | null;
  name: string;
  firstTripDate: string | null;
  categoryTotals: CategoryRow[];
  total: number;
}

const sortCategories = (rows: CategoryRow[]): CategoryRow[] =>
  [...rows].sort((a, b) => {
    if (a.category === "Uncategorized") return 1;
    if (b.category === "Uncategorized") return -1;
    return a.category.localeCompare(b.category);
  });

export const TaxesTab = ({ expenses, trips, vehicles }: TaxesTabProps) => {
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    expenses.forEach((e) => {
      const y = new Date(e.expense_date).getFullYear();
      if (!isNaN(y)) years.add(y);
    });
    trips.forEach((t) => {
      const d = t.start_date || t.end_date;
      if (d) {
        const y = new Date(d).getFullYear();
        if (!isNaN(y)) years.add(y);
      }
    });
    if (years.size === 0) years.add(new Date().getFullYear());
    return Array.from(years).sort((a, b) => b - a);
  }, [expenses, trips]);

  const STORAGE_KEY = "taxes-tab-selected-year";
  const [selectedYear, setSelectedYearState] = useState<number>(() => {
    if (typeof window !== "undefined") {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      const parsed = stored ? Number(stored) : NaN;
      if (!isNaN(parsed) && availableYears.includes(parsed)) return parsed;
    }
    return availableYears[0];
  });

  const setSelectedYear = (year: number) => {
    setSelectedYearState(year);
    if (typeof window !== "undefined") {
      sessionStorage.setItem(STORAGE_KEY, String(year));
    }
  };

  const yearExpenses = useMemo(
    () => expenses.filter((e) => new Date(e.expense_date).getFullYear() === selectedYear),
    [expenses, selectedYear],
  );

  const yearTrips = useMemo(
    () =>
      trips.filter((t) => {
        const d = t.start_date || t.end_date;
        return d ? new Date(d).getFullYear() === selectedYear : false;
      }),
    [trips, selectedYear],
  );

  const vehicleMap = useMemo(() => {
    const map = new Map<string, string>();
    vehicles.forEach((v) => map.set(v.id, v.name));
    return map;
  }, [vehicles]);

  // First trip start date per vehicle (across ALL trips, not just selected year)
  const firstTripDateByVehicle = useMemo(() => {
    const map = new Map<string, string>();
    trips.forEach((t) => {
      if (!t.vehicle_id) return;
      const d = t.start_date || t.trip_start || t.end_date || t.trip_end;
      if (!d) return;
      const existing = map.get(t.vehicle_id);
      if (!existing || new Date(d) < new Date(existing)) {
        map.set(t.vehicle_id, d);
      }
    });
    return map;
  }, [trips]);

  // Build per-vehicle summaries with category breakdowns
  const { vehicleSummaries, unlinkedSummary, grandTotal } = useMemo(() => {
    // vehicleId -> (category -> total)
    const perVehicle = new Map<string, Map<string, number>>();
    const unlinked = new Map<string, number>();

    yearExpenses.forEach((e) => {
      const cat = e.tax_category || "Uncategorized";
      const amt = Number(e.amount);
      if (e.vehicle_id) {
        if (!perVehicle.has(e.vehicle_id)) perVehicle.set(e.vehicle_id, new Map());
        const m = perVehicle.get(e.vehicle_id)!;
        m.set(cat, (m.get(cat) || 0) + amt);
      } else {
        unlinked.set(cat, (unlinked.get(cat) || 0) + amt);
      }
    });

    const summaries: VehicleSummary[] = Array.from(perVehicle.entries()).map(([vid, catMap]) => {
      const rows = sortCategories(
        Array.from(catMap.entries()).map(([category, amount]) => ({ category, amount })),
      );
      const total = rows.reduce((s, r) => s + r.amount, 0);
      return {
        vehicleId: vid,
        name: vehicleMap.get(vid) || "Unknown Vehicle",
        firstTripDate: firstTripDateByVehicle.get(vid) || null,
        categoryTotals: rows,
        total,
      };
    });

    summaries.sort((a, b) => a.name.localeCompare(b.name));

    let unlinkedSum: VehicleSummary | null = null;
    if (unlinked.size > 0) {
      const rows = sortCategories(
        Array.from(unlinked.entries()).map(([category, amount]) => ({ category, amount })),
      );
      unlinkedSum = {
        vehicleId: null,
        name: "Other equipment - No vehicle linked",
        firstTripDate: null,
        categoryTotals: rows,
        total: rows.reduce((s, r) => s + r.amount, 0),
      };
    }

    const grand =
      summaries.reduce((s, v) => s + v.total, 0) + (unlinkedSum ? unlinkedSum.total : 0);

    return { vehicleSummaries: summaries, unlinkedSummary: unlinkedSum, grandTotal: grand };
  }, [yearExpenses, vehicleMap, firstTripDateByVehicle]);

  // Mileage by vehicle
  const mileageSummary = useMemo(() => {
    const data: Record<string, { expenseMiles: number; tripMiles: number; name: string }> = {};

    const ensure = (vid: string) => {
      if (!data[vid]) data[vid] = { expenseMiles: 0, tripMiles: 0, name: vehicleMap.get(vid) || "Unknown Vehicle" };
    };

    yearExpenses.forEach((e) => {
      if (e.vehicle_id && e.mileage) {
        ensure(e.vehicle_id);
        data[e.vehicle_id].expenseMiles += Number(e.mileage);
      }
    });

    yearTrips.forEach((t) => {
      if (t.vehicle_id && t.distance_traveled) {
        ensure(t.vehicle_id);
        data[t.vehicle_id].tripMiles += Number(t.distance_traveled);
      }
    });

    return Object.values(data).sort((a, b) => a.name.localeCompare(b.name));
  }, [yearExpenses, yearTrips, vehicleMap]);

  const totalExpenseMiles = mileageSummary.reduce((s, v) => s + v.expenseMiles, 0);
  const totalTripMiles = mileageSummary.reduce((s, v) => s + v.tripMiles, 0);

  const hasAnyData = vehicleSummaries.length > 0 || unlinkedSummary !== null;

  const renderCategoryRows = (rows: CategoryRow[]) =>
    rows.map((row) => (
      <div key={row.category} className="flex items-center justify-between py-1.5 border-b border-border">
        <span
          className={`text-sm ${row.category === "Uncategorized" ? "italic text-muted-foreground" : "text-foreground"}`}
        >
          {row.category}
        </span>
        <span className="text-sm font-medium tabular-nums text-foreground">
          {formatCurrency(row.amount)}
        </span>
      </div>
    ));

  return (
    <div className="space-y-6">
      {/* Year selector */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-muted-foreground">Tax Year</label>
        <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {availableYears.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Section heading */}
      <h2 className="text-2xl font-semibold tracking-tight">
        Tax Summary by Vehicle — <span className="text-destructive font-bold">{selectedYear}</span>
      </h2>

      <p className="text-sm text-muted-foreground">
        Each vehicle is shown with its deductible expenses broken down by tax category — matching how
        most tax forms ask you to report per asset.
      </p>

      {!hasAnyData ? (
        <Card>
          <CardContent className="py-8">
            <p className="text-sm text-muted-foreground italic">No expenses recorded for {selectedYear}.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Per-vehicle cards */}
          {vehicleSummaries.map((v) => {
            const date = formatDate(v.firstTripDate);
            return (
              <Card key={v.vehicleId ?? "unknown"}>
                <CardHeader>
                  <CardTitle className="text-xl">{v.name}</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    {date ? `First trip: ${date}` : "No trips recorded"}
                  </p>
                </CardHeader>
                <CardContent className="space-y-1">
                  {renderCategoryRows(v.categoryTotals)}
                  <div className="flex items-center justify-between pt-3 border-t-2 border-border">
                    <span className="text-sm font-bold text-foreground">Vehicle Total</span>
                    <span className="text-sm font-bold tabular-nums text-foreground">
                      {formatCurrency(v.total)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Unlinked / other equipment card */}
          {unlinkedSummary && (
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">{unlinkedSummary.name}</CardTitle>
                <p className="text-xs text-muted-foreground italic">
                  Review your expenses to check whether any of these actually pertain to a specific
                  vehicle and should be linked to it.
                </p>
              </CardHeader>
              <CardContent className="space-y-1">
                {renderCategoryRows(unlinkedSummary.categoryTotals)}
                <div className="flex items-center justify-between pt-3 border-t-2 border-border">
                  <span className="text-sm font-bold text-foreground">Total</span>
                  <span className="text-sm font-bold tabular-nums text-foreground">
                    {formatCurrency(unlinkedSummary.total)}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Fleet Total card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">
                Fleet Total — <span className="text-destructive font-bold">{selectedYear}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-foreground">Grand Total Deductible Expenses</span>
                <span className="text-base font-bold tabular-nums text-foreground">
                  {formatCurrency(grandTotal)}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Footnotes */}
          <div className="space-y-2 px-1">
            <p className="text-xs text-muted-foreground leading-relaxed">
              For most Turo hosts, claiming actual expenses typically results in a higher deduction than
              the standard mileage rate. However, consult a tax professional for advice specific to your
              situation. This report is intended to help speed up the process — please double-check that
              all your expense records have the correct Tax Category assigned.
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed italic">
              Note: Initial Repairs made before a vehicle's first rental are treated as Depreciation
              (capitalized costs), not ongoing car expenses.
            </p>
          </div>
        </>
      )}

      {/* Mileage Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">
            Mileage Totals — <span className="text-destructive font-bold">{selectedYear}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {mileageSummary.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No mileage data recorded for {selectedYear}.</p>
          ) : (
            <>
              {/* Header row */}
              <div className="grid grid-cols-3 gap-4 text-xs font-medium text-muted-foreground uppercase tracking-wider pb-1 border-b border-border">
                <span>Vehicle</span>
                <span className="text-right">Expense Mileage</span>
                <span className="text-right">Trip Mileage</span>
              </div>

              {mileageSummary.map((v) => (
                <div key={v.name} className="grid grid-cols-3 gap-4 py-1.5 border-b border-border last:border-0">
                  <span className="text-sm text-foreground">{v.name}</span>
                  <span className="text-sm tabular-nums text-foreground text-right">
                    {v.expenseMiles.toLocaleString()} mi
                  </span>
                  <span className="text-sm tabular-nums text-foreground text-right">
                    {v.tripMiles.toLocaleString()} mi
                  </span>
                </div>
              ))}

              <div className="grid grid-cols-3 gap-4 pt-3 border-t-2 border-border">
                <span className="text-sm font-bold text-foreground">Total</span>
                <span className="text-sm font-bold tabular-nums text-foreground text-right">
                  {totalExpenseMiles.toLocaleString()} mi
                </span>
                <span className="text-sm font-bold tabular-nums text-foreground text-right">
                  {totalTripMiles.toLocaleString()} mi
                </span>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
